import React, { useState, useCallback, useRef, useEffect } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Divider from '@mui/material/Divider';
import { useBarcodeScanner } from './useBarcodeScanner';
import PalletDetailCard, { PalletData } from './PalletDetailCard';
import ManualScanInput from './ManualScanInput';
import { api } from '../../config/api';

type WorkflowState = 'aisle-select' | 'awaiting-pallet' | 'location-confirm' | 'pick-task';
type PickSubState = 'scan-sscc' | 'scan-dropoff';

interface LocationRecommendation {
  id: number;
  location_code: string;
  bay: string;
  level: string;
}

interface PickTask {
  id: number;
  pallet_sscc: string;
  location_code: string;
  bay: string;
  level: string;
  sequence_order: number;
  job_number: string;
  customer_code: string;
  wave_id: string;
  status: string;
}

const AISLES = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N'];
const OPERATOR_NAME_KEY = 'wms_operator_name';

/**
 * iPad-optimized VNA putaway screen with interleaved pick tasks.
 *
 * Four-state workflow:
 * 1. Aisle Selection - operator taps aisle button to check in
 * 2. Awaiting Pallet Scan - scan pallet SSCC to get location recommendation
 * 3. Location Confirmation - scan location barcode to confirm putaway
 * 4. Pick Task - after putaway, show assigned pick tasks (if any) before next putaway
 *
 * Interleaved flow: putaway -> pick -> putaway -> pick (no empty trips).
 * No manual SSCC input -- scanner-only workflow.
 */
export default function PutawayScreen() {
  const [workflowState, setWorkflowState] = useState<WorkflowState>('aisle-select');
  const [selectedAisle, setSelectedAisle] = useState<string | null>(null);
  const [operatorName, setOperatorName] = useState<string>(
    () => localStorage.getItem(OPERATOR_NAME_KEY) || ''
  );
  const [showNameDialog, setShowNameDialog] = useState(false);
  const [pendingAisle, setPendingAisle] = useState<string | null>(null);
  const [nameInput, setNameInput] = useState('');

  const [palletData, setPalletData] = useState<PalletData | null>(null);
  const [, setScannedSscc] = useState<string | null>(null);
  const scannedSsccRef = useRef<string | null>(null);
  const [locationRec, setLocationRec] = useState<LocationRecommendation | null>(null);

  const [pickTasks, setPickTasks] = useState<PickTask[]>([]);
  const pickTasksRef = useRef<PickTask[]>([]);
  const [currentPickIndex, setCurrentPickIndex] = useState(0);
  const currentPickIndexRef = useRef(0);
  const [showingPick, setShowingPick] = useState(false);
  const [pickSubState, setPickSubState] = useState<PickSubState>('scan-sscc');
  const pickSubStateRef = useRef<PickSubState>('scan-sscc');
  const [pickSsccError, setPickSsccError] = useState<string | null>(null);

  const [confirmationMessage, setConfirmationMessage] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const workflowStateRef = useRef<WorkflowState>('aisle-select');
  const selectedAisleRef = useRef<string | null>(null);
  const operatorNameRef = useRef<string>(operatorName);

  // Keep refs in sync with state
  useEffect(() => {
    workflowStateRef.current = workflowState;
  }, [workflowState]);
  useEffect(() => {
    selectedAisleRef.current = selectedAisle;
  }, [selectedAisle]);
  useEffect(() => {
    operatorNameRef.current = operatorName;
  }, [operatorName]);
  useEffect(() => {
    pickTasksRef.current = pickTasks;
  }, [pickTasks]);
  useEffect(() => {
    currentPickIndexRef.current = currentPickIndex;
  }, [currentPickIndex]);

  useEffect(() => {
    pickSubStateRef.current = pickSubState;
  }, [pickSubState]);

  const fetchPickTasks = useCallback(async (aisle: string, operator: string) => {
    try {
      const response = await api.get(
        `/wms/tasks/mine?operator=${encodeURIComponent(operator)}&aisle=${encodeURIComponent(aisle)}`
      );
      if (response.data.success && response.data.data?.tasks) {
        const tasks = response.data.data.tasks;
        setPickTasks(tasks);
        pickTasksRef.current = tasks;
      } else {
        setPickTasks([]);
        pickTasksRef.current = [];
      }
    } catch {
      // Non-blocking: pick task fetch failure should not break putaway
      setPickTasks([]);
      pickTasksRef.current = [];
    }
    setCurrentPickIndex(0);
    currentPickIndexRef.current = 0;
    setShowingPick(false);
  }, []);

  const handleAisleSelect = useCallback(async (aisle: string) => {
    if (!operatorName) {
      setPendingAisle(aisle);
      setShowNameDialog(true);
      return;
    }

    try {
      await api.post('/wms/putaway/aisle-checkin', {
        aisle,
        operator_name: operatorName,
      });
      setSelectedAisle(aisle);
      selectedAisleRef.current = aisle;
      setWorkflowState('awaiting-pallet');
      workflowStateRef.current = 'awaiting-pallet';
      // Reset any prior pallet/location state
      setPalletData(null);
      setScannedSscc(null);
      scannedSsccRef.current = null;
      setLocationRec(null);
      setConfirmationMessage(null);
      setWarnings([]);
      // Fetch pick tasks for this aisle
      fetchPickTasks(aisle, operatorName);
    } catch (err: any) {
      const msg =
        err?.response?.data?.error ||
        err?.message ||
        'Failed to check in to aisle';
      setErrorMessage(msg);
    }
  }, [operatorName, fetchPickTasks]);

  const handleNameSubmit = useCallback(() => {
    if (!nameInput.trim()) return;
    const name = nameInput.trim();
    setOperatorName(name);
    localStorage.setItem(OPERATOR_NAME_KEY, name);
    setShowNameDialog(false);
    setNameInput('');
    if (pendingAisle) {
      // Re-trigger aisle select with name now set
      // Use setTimeout to ensure state update propagates
      const aisle = pendingAisle;
      setPendingAisle(null);
      setTimeout(() => {
        // Inline the checkin logic since operatorName may not be updated yet
        api.post('/wms/putaway/aisle-checkin', {
          aisle,
          operator_name: name,
        }).then(() => {
          setSelectedAisle(aisle);
          selectedAisleRef.current = aisle;
          setWorkflowState('awaiting-pallet');
          workflowStateRef.current = 'awaiting-pallet';
          setPalletData(null);
          setScannedSscc(null);
          scannedSsccRef.current = null;
          setLocationRec(null);
          setConfirmationMessage(null);
          setWarnings([]);
          // Fetch pick tasks for this aisle
          fetchPickTasks(aisle, name);
        }).catch((err: any) => {
          const msg =
            err?.response?.data?.error ||
            err?.message ||
            'Failed to check in to aisle';
          setErrorMessage(msg);
        });
      }, 0);
    }
  }, [nameInput, pendingAisle, fetchPickTasks]);

  const handlePalletScan = useCallback(async (sscc: string) => {
    const aisle = selectedAisleRef.current;
    if (!aisle) return;

    setConfirmationMessage(null);
    setWarnings([]);
    setScannedSscc(sscc);
    scannedSsccRef.current = sscc;

    try {
      // Fetch pallet details and nearest location in parallel
      const [palletResponse, locResponse] = await Promise.all([
        api.get(`/wms/pallets/${sscc}`).catch(() => null),
        api.get(`/wms/putaway/nearest-location?aisle=${aisle}`),
      ]);

      if (locResponse.data.success && locResponse.data.data) {
        setLocationRec(locResponse.data.data);
      } else {
        setLocationRec(null);
      }

      // Build pallet data: use enriched API data if available, fallback to minimal
      let fetchedPallet: PalletData = {
        sscc,
        status: 'at_drop_zone',
        job_number: null,
        customer_code: null,
        customer_name: null,
        product_code: null,
        product_description: null,
        forme_number: null,
        quantity: null,
        pallet_number: null,
        total_pallets: null,
        machine_code: null,
        packed_at: null,
        location: null,
        is_rescan: false,
        has_job_link: false,
      };
      if (palletResponse?.data?.success && palletResponse.data.data) {
        const d = palletResponse.data.data;
        fetchedPallet = {
          sscc: d.sscc,
          status: d.status,
          job_number: d.job_number,
          customer_code: d.customer_code || null,
          customer_name: d.customer_name,
          product_code: d.product_code || null,
          product_description: d.product_description,
          forme_number: d.forme_number || null,
          quantity: d.quantity,
          pallet_number: d.pallet_number,
          total_pallets: d.total_pallets,
          machine_code: d.machine_code || null,
          packed_at: d.packed_at || null,
          location: d.location || null,
          is_rescan: false,
          has_job_link: d.has_job_link,
        };
      }
      setPalletData(fetchedPallet);

      setWorkflowState('location-confirm');
      workflowStateRef.current = 'location-confirm';
    } catch (err: any) {
      const msg =
        err?.response?.data?.error ||
        err?.message ||
        'Failed to get location recommendation';
      setErrorMessage(msg);
    }
  }, []);

  const handleLocationScan = useCallback(async (locationCode: string) => {
    const sscc = scannedSsccRef.current;
    const aisle = selectedAisleRef.current;
    if (!sscc || !aisle) return;

    try {
      const response = await api.post('/wms/putaway/confirm', {
        sscc,
        location_code: locationCode,
        operator_aisle: aisle,
      });

      if (response.data.success) {
        const data = response.data.data;
        setConfirmationMessage(`Putaway confirmed at ${data.location || locationCode}`);
        if (data.warnings && data.warnings.length > 0) {
          setWarnings(data.warnings);
        }
        // After putaway confirmation: show pick tasks if available, else return to awaiting-pallet
        setTimeout(() => {
          setPalletData(null);
          setScannedSscc(null);
          scannedSsccRef.current = null;
          setLocationRec(null);
          setConfirmationMessage(null);
          setWarnings([]);
          const tasks = pickTasksRef.current;
          const idx = currentPickIndexRef.current;
          if (tasks.length > 0 && idx < tasks.length) {
            setShowingPick(true);
            setPickSubState('scan-sscc');
            pickSubStateRef.current = 'scan-sscc';
            setPickSsccError(null);
            setWorkflowState('pick-task');
            workflowStateRef.current = 'pick-task';
          } else {
            setWorkflowState('awaiting-pallet');
            workflowStateRef.current = 'awaiting-pallet';
          }
        }, 2000);
      } else {
        setErrorMessage(response.data.error || 'Putaway confirmation failed');
      }
    } catch (err: any) {
      const msg =
        err?.response?.data?.error ||
        err?.message ||
        'Failed to confirm putaway';
      setErrorMessage(msg);
    }
  }, []);

  const handlePickSsccScan = useCallback((sscc: string) => {
    const tasks = pickTasksRef.current;
    const idx = currentPickIndexRef.current;
    if (idx >= tasks.length) return;

    const expected = tasks[idx].pallet_sscc;
    if (sscc === expected) {
      setPickSsccError(null);
      setPickSubState('scan-dropoff');
      pickSubStateRef.current = 'scan-dropoff';
    } else {
      setPickSsccError(`Wrong pallet! Expected ${expected}`);
    }
  }, []);

  const handlePickDropoffScan = useCallback(async (locationCode: string) => {
    const tasks = pickTasksRef.current;
    const idx = currentPickIndexRef.current;
    if (idx >= tasks.length) return;

    const task = tasks[idx];
    try {
      await api.post('/wms/pick-tasks/complete', {
        task_id: task.id,
        pallet_sscc: task.pallet_sscc,
        dropoff_location: locationCode,
      });

      const nextIndex = idx + 1;
      if (nextIndex < tasks.length) {
        setCurrentPickIndex(nextIndex);
        currentPickIndexRef.current = nextIndex;
        setPickSubState('scan-sscc');
        pickSubStateRef.current = 'scan-sscc';
      } else {
        // All picks done
        setShowingPick(false);
        setCurrentPickIndex(0);
        currentPickIndexRef.current = 0;
        setPickTasks([]);
        pickTasksRef.current = [];
        setPickSubState('scan-sscc');
        pickSubStateRef.current = 'scan-sscc';
        setWorkflowState('awaiting-pallet');
        workflowStateRef.current = 'awaiting-pallet';
      }
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.message || 'Failed to complete pick task';
      setErrorMessage(msg);
    }
  }, []);

  const handleScan = useCallback((code: string) => {
    const isSSCC = code.length >= 18 && /^\d+$/.test(code);
    const state = workflowStateRef.current;

    if (state === 'awaiting-pallet' && isSSCC) {
      handlePalletScan(code);
    } else if (state === 'location-confirm' && !isSSCC) {
      handleLocationScan(code);
    } else if (state === 'location-confirm' && isSSCC) {
      // Operator scanned a different pallet -- go back to pallet scan flow
      handlePalletScan(code);
    } else if (state === 'pick-task') {
      const subState = pickSubStateRef.current;
      if (subState === 'scan-sscc' && isSSCC) {
        handlePickSsccScan(code);
      } else if (subState === 'scan-dropoff' && !isSSCC) {
        handlePickDropoffScan(code);
      } else if (subState === 'scan-dropoff' && isSSCC) {
        setErrorMessage('Scan drop-off location barcode, not a pallet');
      } else if (subState === 'scan-sscc' && !isSSCC) {
        setErrorMessage('Scan the pallet SSCC first');
      }
    }
  }, [handlePalletScan, handleLocationScan, handlePickSsccScan, handlePickDropoffScan]);

  useBarcodeScanner({ minLength: 4, onScan: handleScan });

  const handleChangeAisle = () => {
    setWorkflowState('aisle-select');
    workflowStateRef.current = 'aisle-select';
    setSelectedAisle(null);
    selectedAisleRef.current = null;
    setPalletData(null);
    setScannedSscc(null);
    scannedSsccRef.current = null;
    setLocationRec(null);
    setConfirmationMessage(null);
    setWarnings([]);
    setPickTasks([]);
    setCurrentPickIndex(0);
    setShowingPick(false);
  };

  return (
    <Box
      sx={{
        maxWidth: 800,
        mx: 'auto',
        width: '100%',
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Aisle check-in indicator — layout provides the page title */}
      {selectedAisle && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 1,
            mb: 2,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Aisle {selectedAisle}
          </Typography>
          <Chip
            label="Checked In"
            sx={{ backgroundColor: '#2E7D32', color: '#FFFFFF' }}
            data-testid="checked-in-chip"
          />
        </Box>
      )}

      {/* State 1: Aisle Selection */}
      {workflowState === 'aisle-select' && (
        <Paper
          elevation={0}
          sx={{
            p: 3,
            backgroundColor: '#FFFFFF',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          }}
        >
          <Typography
            variant="h5"
            sx={{ mb: 3, textAlign: 'center', fontWeight: 600 }}
          >
            SELECT AISLE
          </Typography>
          <Grid container spacing={2}>
            {AISLES.map((aisle) => (
              <Grid item xs={3} key={aisle}>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => handleAisleSelect(aisle)}
                  sx={{
                    py: 3,
                    fontSize: '1.5rem',
                    fontWeight: 700,
                    borderColor: '#CB6015',
                    color: '#CB6015',
                    '&:hover': {
                      backgroundColor: '#CB6015',
                      color: '#FFFFFF',
                      borderColor: '#CB6015',
                    },
                  }}
                  data-testid={`aisle-btn-${aisle}`}
                >
                  {aisle}
                </Button>
              </Grid>
            ))}
          </Grid>
        </Paper>
      )}

      {/* State 2: Awaiting Pallet Scan */}
      {workflowState === 'awaiting-pallet' && (
        <Box>
          <Paper
            elevation={0}
            sx={{
              p: 4,
              mb: 3,
              textAlign: 'center',
              backgroundColor: '#FFFFFF',
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            }}
          >
            <Typography
              variant="h5"
              sx={{ fontWeight: 600, color: '#CB6015', mb: 2 }}
            >
              SCAN PALLET
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Scan pallet SSCC barcode to get location recommendation
            </Typography>
            <Box sx={{ mt: 2 }}>
              <ManualScanInput onSubmit={handleScan} placeholder="Type or paste SSCC..." />
            </Box>
          </Paper>

          <Box sx={{ textAlign: 'center' }}>
            <Button
              variant="text"
              onClick={handleChangeAisle}
              sx={{ color: '#9E9E9E' }}
            >
              Change Aisle
            </Button>
          </Box>
        </Box>
      )}

      {/* State 3: Location Confirmation */}
      {workflowState === 'location-confirm' && (
        <Box>
          {/* Pallet detail card */}
          {palletData && (
            <Box sx={{ mb: 3 }}>
              <PalletDetailCard data={palletData} />
            </Box>
          )}

          {/* Location recommendation */}
          <Paper
            elevation={0}
            sx={{
              p: 3,
              mb: 3,
              textAlign: 'center',
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              backgroundColor: confirmationMessage ? '#E8F5E9' : '#FFFFFF',
              border: confirmationMessage
                ? '2px solid #2E7D32'
                : '1px solid #E0E0E0',
              transition: 'background-color 0.2s',
            }}
          >
            {!confirmationMessage && locationRec && (
              <Box>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                  RECOMMENDED
                </Typography>
                <Typography
                  variant="h4"
                  sx={{ fontWeight: 700, color: '#CB6015', mb: 2 }}
                  data-testid="recommended-location"
                >
                  {locationRec.location_code}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Scan location barcode to confirm
                </Typography>
              </Box>
            )}

            {!confirmationMessage && !locationRec && (
              <Alert severity="warning" sx={{ textAlign: 'left' }}>
                No available location found in this aisle
              </Alert>
            )}

            {confirmationMessage && (
              <Box>
                <Typography
                  variant="h4"
                  sx={{ fontWeight: 700, color: '#2E7D32', mb: 1 }}
                  data-testid="confirm-message"
                >
                  {confirmationMessage}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Next: putaway another pallet
                </Typography>
              </Box>
            )}
          </Paper>

          {/* Warnings (non-blocking) */}
          {warnings.length > 0 && (
            <Box sx={{ mb: 3 }}>
              {warnings.map((warning, index) => (
                <Alert
                  key={index}
                  severity="warning"
                  sx={{ mb: 1 }}
                  data-testid="putaway-warning"
                >
                  {warning}
                </Alert>
              ))}
            </Box>
          )}

          <Box sx={{ textAlign: 'center' }}>
            <Button
              variant="text"
              onClick={handleChangeAisle}
              sx={{ color: '#9E9E9E' }}
            >
              Change Aisle
            </Button>
          </Box>
        </Box>
      )}

      {/* State 4: Pick Task Display */}
      {workflowState === 'pick-task' && showingPick && pickTasks.length > 0 && (
        <Box>
          <Card
            elevation={0}
            sx={{
              mb: 3,
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              border: '2px solid #CB6015',
              overflow: 'visible',
            }}
            data-testid="pick-task-card"
          >
            {/* Orange header */}
            <Box
              sx={{
                backgroundColor: '#CB6015',
                color: '#FFFFFF',
                p: 2,
                textAlign: 'center',
              }}
            >
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                PICK TASK {currentPickIndex + 1}/{pickTasks.length}
              </Typography>
            </Box>
            <CardContent sx={{ p: 3 }}>
              {/* Pallet SSCC */}
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 0.5 }}
              >
                PALLET SSCC
              </Typography>
              <Typography
                variant="h4"
                sx={{ fontWeight: 700, mb: 2, fontFamily: 'monospace' }}
                data-testid="pick-sscc"
              >
                {pickTasks[currentPickIndex].pallet_sscc}
              </Typography>

              <Divider sx={{ mb: 2 }} />

              {/* Location */}
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 0.5 }}
              >
                PICK FROM LOCATION
              </Typography>
              <Typography
                variant="h4"
                sx={{ fontWeight: 700, color: '#CB6015', mb: 2 }}
                data-testid="pick-location"
              >
                {pickTasks[currentPickIndex].location_code}
              </Typography>

              <Divider sx={{ mb: 2 }} />

              {/* Job and Customer */}
              <Box sx={{ display: 'flex', gap: 3, mb: 2 }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    JOB
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 600 }}
                    data-testid="pick-job"
                  >
                    {pickTasks[currentPickIndex].job_number}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    CUSTOMER
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 600 }}
                    data-testid="pick-customer"
                  >
                    {pickTasks[currentPickIndex].customer_code}
                  </Typography>
                </Box>
              </Box>

              {/* Pick verification status */}
              {pickSubState === 'scan-sscc' && (
                <Box
                  sx={{
                    p: 2,
                    backgroundColor: '#FFF3E0',
                    borderRadius: '12px',
                    textAlign: 'center',
                    transition: 'background-color 0.2s',
                  }}
                  data-testid="pick-prompt-sscc"
                >
                  <Typography variant="body1" sx={{ fontWeight: 600, color: '#CB6015' }}>
                    STEP 1: SCAN PALLET
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Scan the pallet SSCC barcode to confirm
                  </Typography>
                  {pickSsccError && (
                    <Typography
                      variant="body1"
                      sx={{ mt: 1, color: '#D32F2F', fontWeight: 600 }}
                      data-testid="pick-sscc-error"
                    >
                      {pickSsccError}
                    </Typography>
                  )}
                </Box>
              )}

              {pickSubState === 'scan-dropoff' && (
                <Box
                  sx={{
                    p: 2,
                    backgroundColor: '#E8F5E9',
                    borderRadius: '12px',
                    textAlign: 'center',
                    transition: 'background-color 0.2s',
                  }}
                  data-testid="pick-prompt-dropoff"
                >
                  <Typography variant="body1" sx={{ fontWeight: 600, color: '#2E7D32' }}>
                    STEP 2: SCAN DROP-OFF
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Scan the staging location barcode
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>

          <Box sx={{ textAlign: 'center' }}>
            <Button
              variant="text"
              onClick={handleChangeAisle}
              sx={{ color: '#9E9E9E' }}
            >
              Change Aisle
            </Button>
          </Box>
        </Box>
      )}

      {/* Operator name dialog */}
      <Dialog open={showNameDialog} onClose={() => setShowNameDialog(false)}>
        <DialogTitle>Enter Your Name</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Operator Name"
            value={nameInput}
            onChange={(e) => setNameInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleNameSubmit();
            }}
            sx={{ mt: 1 }}
            data-testid="operator-name-input"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowNameDialog(false)}>Cancel</Button>
          <Button onClick={handleNameSubmit} variant="contained">
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* Error snackbar */}
      <Snackbar
        open={!!errorMessage}
        autoHideDuration={4000}
        onClose={() => setErrorMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity="error"
          onClose={() => setErrorMessage(null)}
          sx={{ width: '100%' }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}
